// Where the dashboard gets its per-deployment settings.
//
// Create React App bakes `process.env.REACT_APP_*` into the bundle at BUILD time. That is fine for
// the cloud, where there is exactly one deployment — but on-premise it would mean rebuilding and
// re-shipping the whole dashboard for every customer's hostname, which destroys the idea of one
// standard package.
//
// So the container writes these at START time instead: nginx/30-config-js.sh in mindlabs-onprem
// generates /config.js from its environment, index.html loads it before the bundle, and it lands on
// `window.mindlabsConfig`.
//
// Everything falls back to the build-time value, so the cloud build and `npm run dev` behave exactly
// as they always have — no /config.js exists there, the global is undefined, and the fallback wins.
// Named `mindlabsConfig`, not `__MINDLABS_CONFIG__`: the repo's lint rules reject the dot form of a
// dangling-underscore name AND the bracket form of a valid identifier, so the two rules leave no way
// to write the original. Both ends of this name are ours, so renaming is the fix rather than a
// suppression — nginx/30-config-js.sh writes the same name.
const runtime = (typeof window !== 'undefined' && window.mindlabsConfig) || {};

// A box is exactly a deployment that was handed a runtime config: nginx writes /config.js at
// container start and the cloud serves no such file, so this needs no variable of its own.
const IS_ONPREM = Object.keys(runtime).length > 0;

export const API_URL = runtime.API_URL || process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Where identity requests go: creating a person, deleting one, groups, and who is in which group.
//
// In the cloud this is the same API as everything else, so it defaults to API_URL and nothing about
// the cloud changes. On a customer's own box it is our cloud's API, and setting it is what turns User
// Management on there.
//
// The reason for the split is that identity and authorisation live in different places. A person's
// ACCOUNT is one Cognito account shared by every deployment — which is already true, because that is
// what they log in with. Their PERMISSIONS are per-deployment, stored in that installation's own
// database, and must stay there.
//
// The alternative was giving the box credentials to Cognito's admin API. Those are granted per user
// pool and cannot be narrowed to one customer, so a box holding them could read, create and delete
// the users of every other customer we have. Sending the request from the browser instead — with the
// person's own login, which they already have — needs no credential on the box at all.
export const CLOUD_API_URL = runtime.CLOUD_API_URL || process.env.REACT_APP_CLOUD_API_URL || '';

export const USERPOOL_ID = runtime.USERPOOL_ID || process.env.REACT_APP_USERPOOL_ID;
export const CLIENT_ID = runtime.CLIENT_ID || process.env.REACT_APP_CLIENT_ID;

// Azure/Microsoft SSO. Absent on a box unless the customer uses it, in which case the "Sign in with
// Microsoft" button has nothing to point at — see FEATURES below.
export const MS_CLIENT_ID = runtime.MS_CLIENT_ID || process.env.REACT_APP_MS_CLIENT_ID;

// Mapbox is fetched from the internet, so a box has no route to it. Empty is the on-premise default.
export const MAPBOX_ACCESS_TOKEN = runtime.MAPBOX_ACCESS_TOKEN || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Per-deployment feature switches, mirroring the backend's FEATURE_* variables. Default ON so the
// cloud is unaffected by their existence: only an explicit false turns one off.
//
// B14 originally dropped maps on-premise on the assumption the box had no internet. It does, so maps
// are available and this is just a switch now. The token check below still matters: our frontend
// tokens are URL-restricted (verified — they 403 from any non-allowed origin), so a customer's box
// hostname must be added to the token's allow-list in the Mapbox dashboard or the map 403s. Hiding it
// when no token is configured is better than showing a broken tile grid.
const features = runtime.FEATURES || {};
export const FEATURES = {
  maps: features.maps !== false && Boolean(MAPBOX_ACCESS_TOKEN),
  // Available wherever identity requests have somewhere to go.
  //
  // In the cloud that is always true. On a box it is exactly "CLOUD_API_URL is set", because that is
  // what lets the page create and manage accounts without the box holding Cognito credentials it must
  // never have — see CLOUD_API_URL above. Unset, the page has nothing to talk to and stays hidden
  // rather than failing when someone opens it.
  //
  // The two are separate expressions on purpose. This used to also require the runtime FEATURES.users
  // flag, which a box feeds from FEATURE_USERS — and FEATURE_USERS must stay false there, because on
  // the BACKEND it decides whether this installation's own API answers account requests, which it must
  // not. So the documented configuration could never show the page, and the only way to make it appear
  // also switched off the seeding in auth-middleware.ts that gives a person the local record alerting
  // needs. One flag cannot mean both "this box administers accounts" and "this box can reach the API
  // that does".
  users: IS_ONPREM ? Boolean(CLOUD_API_URL) : features.users !== false,
};
